import { useMutation, useQuery, useQueryClient } from 'react-query';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';

export interface CopilotConfigDto {
  id: string;
  agencyId: number;
  workflowId: string | null;
  stepIds: string[];
  enabled: boolean;
  detectClientName: boolean;
  detectCustomFields: boolean;
  analyzeInterestScore: boolean;
  suggestReplies: boolean;
  salesCoaching: boolean;
  captureTravelers: boolean;
  automaticTravelerCapture: boolean;
  travelerCaptureChannels: string[];
  useExternalApis: boolean;
  externalApiFacets: string[];
  targetCustomFields: string[];
  passiveModel: string;
  activeModel: string;
  stepContext: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CopilotConfigFormValues {
  workflowId: string | null;
  stepIds: string[];
  enabled: boolean;
  detectClientName: boolean;
  detectCustomFields: boolean;
  analyzeInterestScore: boolean;
  suggestReplies: boolean;
  salesCoaching: boolean;
  captureTravelers: boolean;
  automaticTravelerCapture: boolean;
  travelerCaptureChannels: string[];
  useExternalApis: boolean;
  externalApiFacets: string[];
  targetCustomFields: string[];
  passiveModel: string;
  activeModel: string;
  stepContext: string;
}

export const useCopilotConfigs = (agencyId: number) =>
  useQuery({
    queryKey: [QueryKeys.getCopilotConfigs, agencyId],
    queryFn: async (): Promise<CopilotConfigDto[]> => {
      const response = await client.get<CopilotConfigDto[]>(
        `agencies/${agencyId}/copilot-configs`,
      );
      return response.data;
    },
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: true,
  });

export const useSaveCopilotConfig = (agencyId: number, configId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: CopilotConfigFormValues) => {
      const payload = {
        workflowId: values.workflowId || null,
        // stepIds solo tienen sentido con un workflow seleccionado.
        stepIds: values.workflowId ? values.stepIds : [],
        enabled: values.enabled,
        detectClientName: values.detectClientName,
        detectCustomFields: values.detectCustomFields,
        analyzeInterestScore: values.analyzeInterestScore,
        suggestReplies: values.suggestReplies,
        salesCoaching: values.salesCoaching,
        captureTravelers: values.captureTravelers,
        automaticTravelerCapture: values.automaticTravelerCapture,
        travelerCaptureChannels: values.travelerCaptureChannels,
        useExternalApis: values.useExternalApis,
        externalApiFacets: values.externalApiFacets,
        // Solo tiene sentido con detección de campos activa y un workflow elegido.
        targetCustomFields:
          values.detectCustomFields && values.workflowId
            ? values.targetCustomFields
            : [],
        passiveModel: values.passiveModel,
        activeModel: values.activeModel,
        stepContext: values.stepContext,
      };
      if (configId) {
        await client.patch(
          `agencies/${agencyId}/copilot-configs/${configId}`,
          payload,
        );
      } else {
        await client.post(`agencies/${agencyId}/copilot-configs`, payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCopilotConfigs]);
    },
  });
};

export const useDeleteCopilotConfig = (agencyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.delete(`agencies/${agencyId}/copilot-configs/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCopilotConfigs]);
    },
  });
};

// Catálogo de facets de travel-intelligence (config + botones on-demand).
export const TRAVEL_FACET_OPTIONS: { value: string; label: string }[] = [
  { value: 'weather', label: 'Clima' },
  { value: 'documentation', label: 'Documentación' },
  { value: 'money', label: 'Moneda' },
  { value: 'packages', label: 'Paquetes' },
  { value: 'highlights', label: 'Qué ver' },
  { value: 'events', label: 'Eventos' },
  { value: 'risk', label: 'Alertas' },
  { value: 'socialProof', label: 'Valoraciones' },
];

/** Facets habilitados para el caso (para renderizar los botones del panel). */
export const useCopilotFacets = (caseId?: number) =>
  useQuery({
    queryKey: ['copilotFacets', caseId],
    queryFn: async (): Promise<string[]> => {
      const response = await client.get<string[]>(
        `copilot/cases/${caseId}/travel-intelligence/facets`,
      );
      return response.data;
    },
    enabled: Boolean(caseId),
  });

export interface UpdateCaseCustomFieldValuePayload {
  caseId: number;
  fieldBlockId: string;
  fieldName: string;
  value: string | number | boolean;
}

/**
 * Aplica el valor sugerido por el copiloto a un custom field del caso.
 * Reusa el endpoint existente PUT /case/:id/custom-fields.
 */
export const useUpdateCaseCustomFieldValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      caseId,
      fieldBlockId,
      fieldName,
      value,
    }: UpdateCaseCustomFieldValuePayload) =>
      client.put(`/case/${caseId}/custom-fields`, [
        { fieldBlockId, fieldName, value },
      ]),
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCaseCustomFields]);
      queryClient.invalidateQueries([QueryKeys.getCaseById]);
    },
  });
};
